document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('motion-ready');

document.addEventListener('DOMContentLoaded', () => {
  const body = document.body;
  const menuButton = document.querySelector('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const searchButton = document.querySelector('[data-search-toggle]');
  const searchPanel = document.querySelector('[data-search-panel]');
  const searchClose = document.querySelector('[data-search-close]');

  const setExpanded = (button, panel, expanded) => {
    if (!button || !panel) return;
    button.setAttribute('aria-expanded', String(expanded));
    panel.hidden = !expanded;
  };

  const closeOverlays = () => {
    setExpanded(menuButton, mobileMenu, false);
    setExpanded(searchButton, searchPanel, false);
    body.classList.remove('overlay-open');
  };

  menuButton?.addEventListener('click', () => {
    const opening = menuButton.getAttribute('aria-expanded') !== 'true';
    closeOverlays();
    setExpanded(menuButton, mobileMenu, opening);
    body.classList.toggle('overlay-open', opening);
  });

  searchButton?.addEventListener('click', () => {
    const opening = searchButton.getAttribute('aria-expanded') !== 'true';
    closeOverlays();
    setExpanded(searchButton, searchPanel, opening);
    body.classList.toggle('overlay-open', opening);
    if (opening) searchPanel.querySelector('input[type="search"]')?.focus();
  });

  searchClose?.addEventListener('click', closeOverlays);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeOverlays();
  });

  document.querySelectorAll('[data-quantity]').forEach((wrapper) => {
    const input = wrapper.querySelector('input[type="number"]');
    wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
    });
    wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      input.value = Math.min(Number(input.max || 999), Number(input.value || 1) + 1);
    });
  });

  initProductSections(document);
  initLailaMotion(document);
});

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initLailaMotion(scope) {
  initReveals(scope);
  initParallax(scope);
  initQuoteSliders(scope);
  initHorizontalSliders(scope);
}

function initReveals(scope) {
  const items = scope.querySelectorAll('[data-reveal]:not([data-motion-ready])');
  if (!items.length) return;

  items.forEach((item) => item.setAttribute('data-motion-ready', 'true'));

  if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
    items.forEach((item) => item.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  items.forEach((item) => observer.observe(item));
}

function initParallax(scope) {
  const items = [...scope.querySelectorAll('[data-parallax]:not([data-parallax-ready])')];
  if (!items.length || prefersReducedMotion.matches || window.innerWidth < 750) return;

  items.forEach((item) => item.setAttribute('data-parallax-ready', 'true'));
  let ticking = false;

  const update = () => {
    const viewportCenter = window.innerHeight / 2;
    items.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const elementCenter = rect.top + rect.height / 2;
      const offset = Math.max(-22, Math.min(22, (viewportCenter - elementCenter) * 0.045));
      item.style.setProperty('--parallax-y', `${offset}px`);
    });
    ticking = false;
  };

  const requestTick = () => {
    if (!ticking) requestAnimationFrame(update);
    ticking = true;
  };

  update();
  window.addEventListener('scroll', requestTick, { passive: true });
}

function initQuoteSliders(scope) {
  scope.querySelectorAll('[data-quote-slider]:not([data-slider-ready])').forEach((slider) => {
    slider.setAttribute('data-slider-ready', 'true');
    const slides = [...slider.querySelectorAll('[data-quote-slide]')];
    const dots = [...slider.querySelectorAll('[data-quote-dot]')];
    const previous = slider.querySelector('[data-quote-prev]');
    const next = slider.querySelector('[data-quote-next]');
    if (slides.length < 2) return;

    let active = 0;
    let timer;
    const delay = Number(slider.dataset.autoplay || 6) * 1000;

    const show = (index) => {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === active;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', String(!selected));
      });
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === active);
        dot.setAttribute('aria-current', dotIndex === active ? 'true' : 'false');
      });
    };

    const start = () => {
      window.clearInterval(timer);
      if (!prefersReducedMotion.matches && slider.dataset.autoRotate === 'true') {
        timer = window.setInterval(() => show(active + 1), delay);
      }
    };

    previous?.addEventListener('click', () => { show(active - 1); start(); });
    next?.addEventListener('click', () => { show(active + 1); start(); });
    dots.forEach((dot, index) => dot.addEventListener('click', () => { show(index); start(); }));
    slider.addEventListener('mouseenter', () => window.clearInterval(timer));
    slider.addEventListener('mouseleave', start);
    show(0);
    start();
  });
}

function initHorizontalSliders(scope) {
  scope.querySelectorAll('[data-horizontal-slider]:not([data-horizontal-ready])').forEach((slider) => {
    slider.setAttribute('data-horizontal-ready', 'true');
    const track = slider.querySelector('[data-horizontal-track]');
    if (!track) return;
    slider.querySelector('[data-horizontal-prev]')?.addEventListener('click', () => {
      track.scrollBy({ left: -track.clientWidth * 0.72, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    });
    slider.querySelector('[data-horizontal-next]')?.addEventListener('click', () => {
      track.scrollBy({ left: track.clientWidth * 0.72, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    });
  });
}

function initProductSections(scope) {
  scope.querySelectorAll('[data-product-root]:not([data-product-ready])').forEach((root) => {
    root.setAttribute('data-product-ready', 'true');
    const form = root.querySelector('[data-product-form]');
    const variantsNode = root.querySelector('[data-product-variants]');
    const gallery = root.querySelector('[data-product-gallery]');
    const galleryItems = [...root.querySelectorAll('[data-product-media]')];
    const galleryTotal = root.querySelector('[data-gallery-total]');
    const colorOptionEl = root.querySelector('[data-product-option].product-option--color');
    const currentMedia = root.querySelector('[data-gallery-current]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const addLabel = root.querySelector('[data-add-label]');
    const mobileAdd = root.querySelector('[data-mobile-add]');
    const mobileBar = root.querySelector('[data-mobile-purchase-bar]');
    const status = root.querySelector('[data-product-status]');
    const variantInput = root.querySelector('[data-variant-id]');
    const availability = root.querySelector('.product-availability');
    const sku = root.querySelector('[data-product-sku]');
    const currentPrice = root.querySelector('[data-current-price]');
    const mobilePrice = root.querySelector('[data-mobile-price]');
    const priceWrap = root.querySelector('[data-product-price]');
    const lowStockThreshold = Number(root.dataset.lowStockThreshold || 5);
    let variants = [];

    try {
      variants = JSON.parse(variantsNode?.textContent || '[]');
    } catch (error) {
      variants = [];
    }

    const selectedOptions = () => [...root.querySelectorAll('[data-product-option]')]
      .map((option) => option.querySelector('input:checked')?.value || '');

    const findSelectedVariant = () => {
      const selected = selectedOptions();
      return variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
    };

    // Only show gallery images that belong to the selected color (plus any
    // images that aren't tied to a specific color, which always stay visible).
    const filterGalleryByColor = () => {
      if (!galleryItems.length) return;
      const selectedColor = colorOptionEl?.querySelector('input:checked')?.value;
      let visibleCount = 0;
      galleryItems.forEach((item) => {
        const colors = (item.dataset.mediaColors || '').split('||').filter(Boolean);
        const matches = !selectedColor || colors.length === 0 || colors.includes(selectedColor);
        item.hidden = !matches;
        if (matches) visibleCount += 1;
      });
      if (galleryTotal) galleryTotal.textContent = String(visibleCount || galleryItems.length);
      if (currentMedia) currentMedia.textContent = '01';
      gallery?.scrollTo({ left: 0, behavior: 'auto' });
    };

    const updateComparePrice = (variant) => {
      let compare = root.querySelector('[data-compare-price]');
      const showCompare = Number(variant.compareAtPrice) > Number(variant.price);
      if (showCompare && !compare) {
        compare = document.createElement('span');
        compare.className = 'price-compare';
        compare.setAttribute('data-compare-price', '');
        priceWrap?.prepend(compare);
      }
      if (compare) {
        compare.textContent = showCompare ? variant.compareAtFormatted : '';
        compare.hidden = !showCompare;
      }
    };

    const updateOptionAvailability = () => {
      const selected = selectedOptions();
      root.querySelectorAll('[data-product-option]').forEach((option, optionIndex) => {
        option.querySelectorAll('input[type="radio"]').forEach((input) => {
          const hypothetical = [...selected];
          hypothetical[optionIndex] = input.value;
          const possible = variants.some((variant) => variant.available && variant.options.every((value, index) => value === hypothetical[index]));
          input.disabled = !possible && !input.checked;
        });
      });
    };

    const updateVariant = (variant) => {
      const available = Boolean(variant?.available);
      if (variantInput) variantInput.value = variant?.id || '';
      if (currentPrice) currentPrice.textContent = variant?.priceFormatted || 'Unavailable';
      if (mobilePrice) mobilePrice.textContent = variant?.priceFormatted || 'Unavailable';
      if (variant) updateComparePrice(variant);

      [addButton, mobileAdd].forEach((button) => {
        if (!button) return;
        button.disabled = !available;
      });
      if (addLabel) addLabel.textContent = available ? 'Add to cart' : 'Sold out';
      if (mobileAdd) mobileAdd.textContent = available ? 'Add to cart' : 'Sold out';

      if (availability) {
        availability.textContent = available ? 'In stock' : 'Sold out';
        availability.classList.toggle('is-sold-out', !available);
      }
      if (sku && variant) sku.textContent = variant.sku || '—';

      if (status) {
        if (!variant) status.textContent = 'This combination is unavailable.';
        else if (!available) status.textContent = 'This option is currently sold out.';
        else if (variant.inventoryManagement && variant.inventoryQuantity > 0 && variant.inventoryQuantity <= lowStockThreshold) {
          status.textContent = `Only ${variant.inventoryQuantity} left — almost gone.`;
        } else status.textContent = 'Ready to become part of your story.';
      }

      if (variant?.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      if (variant?.featuredMediaId && window.innerWidth <= 720) {
        root.querySelector(`[data-media-id="${variant.featuredMediaId}"]`)?.scrollIntoView({ behavior: prefersReducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      }
      updateOptionAvailability();
    };

    root.querySelectorAll('[data-product-option] input').forEach((input) => {
      input.addEventListener('change', () => {
        const option = input.closest('[data-product-option]');
        const selectedLabel = option?.querySelector('[data-selected-option]');
        if (selectedLabel) selectedLabel.textContent = input.value;
        updateVariant(findSelectedVariant());
        if (option === colorOptionEl) filterGalleryByColor();
      });
    });

    updateOptionAvailability();
    filterGalleryByColor();
    mobileAdd?.addEventListener('click', () => form?.requestSubmit(addButton));

    if (mobileBar && addButton && 'IntersectionObserver' in window) {
      const purchaseObserver = new IntersectionObserver(([entry]) => {
        mobileBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      purchaseObserver.observe(addButton);
    }

    const scrollGallery = (direction) => {
      if (!gallery) return;
      gallery.scrollBy({ left: direction * gallery.clientWidth * .9, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    };
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => scrollGallery(-1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => scrollGallery(1));
    gallery?.addEventListener('scroll', () => {
      if (!currentMedia || !galleryItems.length) return;
      window.requestAnimationFrame(() => {
        const galleryCenter = gallery.scrollLeft + gallery.clientWidth / 2;
        let closest = 0;
        let closestDistance = Infinity;
        let visibleIndex = 0;
        galleryItems.forEach((item) => {
          if (item.hidden) return;
          visibleIndex += 1;
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const distance = Math.abs(itemCenter - galleryCenter);
          if (distance < closestDistance) {
            closest = visibleIndex;
            closestDistance = distance;
          }
        });
        currentMedia.textContent = String(closest || 1).padStart(2, '0');
      });
    }, { passive: true });

    const lightbox = root.querySelector('[data-product-lightbox]');
    const lightboxImage = root.querySelector('[data-lightbox-image]');
    root.querySelectorAll('[data-product-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!lightbox || !lightboxImage) return;
        lightboxImage.src = button.dataset.fullImage;
        lightboxImage.alt = button.dataset.fullAlt || '';
        lightbox.showModal();
      });
    });
    root.querySelector('[data-lightbox-close]')?.addEventListener('click', () => lightbox?.close());
    lightbox?.addEventListener('click', (event) => { if (event.target === lightbox) lightbox.close(); });

    const sizeGuide = root.querySelector('[data-size-guide-modal]');
    root.querySelector('[data-size-guide-open]')?.addEventListener('click', () => sizeGuide?.showModal());
    root.querySelector('[data-size-guide-close]')?.addEventListener('click', () => sizeGuide?.close());
    sizeGuide?.addEventListener('click', (event) => { if (event.target === sizeGuide) sizeGuide.close(); });
  });
}

document.addEventListener('shopify:section:load', (event) => {
  initProductSections(event.target);
  initLailaMotion(event.target);
});
